import { useState, useEffect, useCallback, useRef } from 'react'
import { apiClient } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts'
import type { HRReportResponse, ChartType, ViewMode, HRFilterPresetResponse, HRComparisonResponse, HRComparisonPeriodResponse, HRComparisonPeriodSave, HRAdjustmentResponse, HRExportLogEntry, ScheduledReportEntry, ScheduledReportCreate, ExportTemplate } from '@/types/hr-reporting'
import { REPORTS, COLOR_PRESETS, DEFAULT_COLUMN_LABELS } from '@/types/hr-reporting'
import { isModuleInactiveError } from '@/utils/hrLabels'
import { HrPageHeader } from '@/components/hr/HrPageHeader'
import { HrModuleInactiveNotice } from '@/components/hr/HrModuleInactiveNotice'

const COLORS = ['#3b82f6', '#22c55e', '#f97316', '#ef4444', '#8b5cf6', '#ec4899']

/** hr2-v2 (E9): Zahlen de-CH mit Tausendertrennung */
function fmtNum(n: number, maxDigits = 1): string {
  return n.toLocaleString('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: maxDigits })
}

function todayStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function monthStartStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

export function HRReportPage() {
  const today = todayStr()
  const monthStart = monthStartStr()
  const [activeReport, setActiveReport] = useState<string>(REPORTS[0]?.key ?? 'total_angefragt_besetzt')
  const [dateFrom, setDateFrom] = useState(monthStart)
  const [dateTo, setDateTo] = useState(today)
  const [reportData, setReportData] = useState<HRReportResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // R11 (hr-v3): Deaktiverte Statistik-Seite → Hinweis statt API-403-Banner
  const [moduleInactive, setModuleInactive] = useState(false)

  const [viewMode, setViewMode] = useState<ViewMode>('both')
  const [chartType, setChartType] = useState<ChartType>('bar')
  const [chartData, setChartData] = useState<Array<Record<string, unknown>>>([])

  const [showFilters, setShowFilters] = useState(false)
  const [filterCategory, setFilterCategory] = useState('all')
  const [presets, setPresets] = useState<HRFilterPresetResponse['presets']>([])
  const [presetName, setPresetName] = useState('')

  const [comparisonEnabled, setComparisonEnabled] = useState(false)
  const [comparisonData, setComparisonData] = useState<HRComparisonResponse | null>(null)
  const [comparisonPeriods, setComparisonPeriods] = useState<HRComparisonPeriodResponse[]>([])
  const [selectedComparison, setSelectedComparison] = useState<string>('')

  const [showAdjustments, setShowAdjustments] = useState(false)
  const [adjustments, setAdjustments] = useState<Array<{
    id: string; target_type: string; field_name: string; old_value: string | null;
    new_value: string; adjustment_reason: string; performed_by_name: string; created_at: string;
  }>>([])

  const [showExports, _setShowExports] = useState(false)
  const [exportHistory, setExportHistory] = useState<HRExportLogEntry[]>([])

  const [showScheduled, _setShowScheduled] = useState(false)
  const [schedules, setSchedules] = useState<ScheduledReportEntry[]>([])
  const [showCreateSchedule, setShowCreateSchedule] = useState(false)
  const [newSchedule, setNewSchedule] = useState<Partial<ScheduledReportCreate>>({
    report_type: 'total_angefragt_besetzt',
    export_format: 'csv',
    frequency: 'weekly',
    day_of_week: 0,
    time_of_day: '08:00',
    recipient_emails: [],
    email_subject_template: 'HR Report: {report_name} - {date_range}',
    attach_report: true,
  })
  const [newScheduleEmail, setNewScheduleEmail] = useState('')

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
  const [templates, setTemplates] = useState<ExportTemplate[]>([])
  const [showTemplateManager, setShowTemplateManager] = useState(false)
  const [activeTemplate, setActiveTemplate] = useState<ExportTemplate | null>(null)
  const [editingCol, setEditingCol] = useState<string | null>(null)
  const [templateTab, setTemplateTab] = useState<'csv' | 'xlsx' | 'pdf'>('csv')
  const [templateName, setTemplateName] = useState('')
  const [templateColors, setTemplateColors] = useState(COLOR_PRESETS[0])
  const [logoPreview, setLogoPreview] = useState<string | null>(null)

  const [page, setPage] = useState(1)
  const pageSize = 15

  const chartRef = useRef<HTMLDivElement>(null)

  const fetchReport = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await apiClient.get<HRReportResponse>(`/api/v1/hr/reports/${activeReport}`, {
        params: { date_from: dateFrom, date_to: dateTo }
      })
      setReportData(data)
      setChartData(transformToChartData(data))
      setPage(1)
    } catch (err: unknown) {
      console.error('Failed to load HR report:', err)
      if (isModuleInactiveError(err)) {
        setModuleInactive(true)
      } else {
        setError(extractErrorMessage(err, {
          loadError: 'Bericht konnte nicht geladen werden',
        }))
      }
      setReportData(null)
    } finally {
      setLoading(false)
    }
  }, [activeReport, dateFrom, dateTo])

  const fetchPresets = useCallback(async () => {
    try {
      const { data } = await apiClient.get<HRFilterPresetResponse>('/api/v1/hr/filter-presets', {
        params: filterCategory !== 'all' ? { category: filterCategory } : undefined
      })
      setPresets(data.presets)
    } catch { /* silently fail */ }
  }, [filterCategory])

  const fetchComparison = useCallback(async () => {
    if (!comparisonEnabled) {
      setComparisonData(null)
      return
    }
    try {
      const { data } = await apiClient.get<HRComparisonResponse>('/api/v1/hr/reports/compare-periods', {
        params: { period_type: 'month', compare_with_previous: true }
      })
      setComparisonData(data)
    } catch { /* silently fail */ }
  }, [comparisonEnabled])

  const fetchComparisonPeriods = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ periods: HRComparisonPeriodResponse[] }>('/api/v1/hr/comparison-periods')
      setComparisonPeriods(data.periods)
    } catch { /* silently fail */ }
  }, [])

  const fetchAdjustments = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ adjustments: HRAdjustmentResponse[] }>('/api/v1/hr/adjustments', {
        params: { limit: 20 }
      })
      setAdjustments(data.adjustments)
    } catch { /* silently fail */ }
  }, [])

  useEffect(() => { fetchReport() }, [fetchReport])
  useEffect(() => { fetchComparison() }, [fetchComparison])
  useEffect(() => {
    if (showFilters) fetchPresets()
  }, [showFilters, fetchPresets])
  useEffect(() => { fetchComparisonPeriods() }, [fetchComparisonPeriods])
  useEffect(() => {
    if (showAdjustments) fetchAdjustments()
  }, [showAdjustments, fetchAdjustments])

  const fetchExportHistory = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ exports: HRExportLogEntry[] }>('/api/v1/hr/exports')
      setExportHistory(data.exports)
    } catch { /* silently fail */ }
  }, [])

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ schedules: ScheduledReportEntry[] }>('/api/v1/hr/scheduled-reports')
      setSchedules(data.schedules)
    } catch { /* silently fail */ }
  }, [])

  const handleCreateSchedule = async () => {
    if (!newSchedule.schedule_name || !newSchedule.recipient_emails?.length) return
    try {
      await apiClient.post('/api/v1/hr/scheduled-reports', newSchedule)
      setNewSchedule({ report_type: 'total_angefragt_besetzt', export_format: 'csv', frequency: 'weekly', day_of_week: 0, time_of_day: '08:00', recipient_emails: [], email_subject_template: 'HR Report: {report_name} - {date_range}', attach_report: true })
      setNewScheduleEmail('')
      setShowCreateSchedule(false)
      fetchSchedules()
    } catch { setError('Geplanten Report konnte nicht erstellt werden') }
  }

  const handleToggleSchedule = async (id: string, active: boolean) => {
    try {
      await apiClient.put(`/api/v1/hr/scheduled-reports/${id}`, { is_active: active })
      fetchSchedules()
    } catch { setError('Status konnte nicht geändert werden') }
  }

  const handleRunScheduleNow = async (id: string) => {
    try {
      await apiClient.post(`/api/v1/hr/scheduled-reports/${id}/run-now`)
      fetchSchedules()
    } catch { setError('Ausführung fehlgeschlagen') }
  }

  const handleDeleteSchedule = async (id: string) => {
    try {
      await apiClient.delete(`/api/v1/hr/scheduled-reports/${id}`)
      fetchSchedules()
    } catch { setError('Löschen fehlgeschlagen') }
  }

  const handleAddScheduleEmail = () => {
    if (!newScheduleEmail.trim()) return
    setNewSchedule(prev => ({
      ...prev,
      recipient_emails: [...(prev.recipient_emails || []), newScheduleEmail.trim()]
    }))
    setNewScheduleEmail('')
  }

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ templates: ExportTemplate[] }>('/api/v1/hr/export-templates')
      setTemplates(data.templates)
      setActiveTemplate(null)
    } catch { /* silently fail */ }
  }, [])

  const handleOpenTemplateManager = (fmt: 'csv' | 'xlsx' | 'pdf') => {
    setTemplateTab(fmt)
    fetchTemplates()
    setShowTemplateManager(true)
  }

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return
    const defaultColMap = Object.keys(DEFAULT_COLUMN_LABELS).reduce((acc, k) => ({ ...acc, [k]: { label: DEFAULT_COLUMN_LABELS[k], visible: k !== 'teacher_id', width_px: 120 } }), {})
    try {
      await apiClient.post('/api/v1/hr/export-templates', {
        template_name: templateName,
        export_format: templateTab,
        is_default: true,
        template_config: { color_scheme: templateColors, freeze_first_row: templateTab === 'xlsx', auto_filter: templateTab === 'xlsx', include_summary_sheet: templateTab === 'xlsx', orientation: 'landscape' },
        column_mapping: defaultColMap,
      })
      setTemplateName('')
      fetchTemplates()
    } catch { setError('Template konnte nicht erstellt werden') }
  }

  const handleUpdateTemplate = async (id: string, updates: Record<string, unknown>) => {
    try {
      await apiClient.put(`/api/v1/hr/export-templates/${id}`, updates)
      fetchTemplates()
    } catch { setError('Update fehlgeschlagen') }
  }

  const handleDeleteTemplate = async (id: string) => {
    try {
      await apiClient.delete(`/api/v1/hr/export-templates/${id}`)
      fetchTemplates()
    } catch { setError('Löschen fehlgeschlagen') }
  }

  const handleUploadLogo = async (file: File) => {
    try {
      const fd = new FormData()
      fd.append('file', file)
      const { data } = await apiClient.post('/api/v1/hr/export-templates/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setLogoPreview(data.path)
    } catch { setError('Logo Upload fehlgeschlagen') }
  }

  const handleResetDefaults = async () => {
    try {
      await apiClient.post('/api/v1/hr/export-templates/reset-defaults')
      fetchTemplates()
    } catch { setError('Reset fehlgeschlagen') }
  }

  useEffect(() => {
    if (showExports) fetchExportHistory()
  }, [showExports, fetchExportHistory])
  useEffect(() => {
    if (showScheduled) fetchSchedules()
  }, [showScheduled, fetchSchedules])

  const handleSavePreset = async () => {
    if (!presetName.trim()) return
    try {
      await apiClient.post('/api/v1/hr/filter-presets', {
        name: presetName,
        category: activeReport,
        filters: { date_from: dateFrom, date_to: dateTo },
        is_public: false
      })
      setPresetName('')
      fetchPresets()
    } catch { setError('Preset konnte nicht gespeichert werden') }
  }

  const handleApplyPreset = async (preset: HRFilterPresetResponse['presets'][number]) => {
    if (preset.category) setActiveReport(preset.category)
    const f = preset.filters as Record<string, string> | undefined
    if (f?.date_from) setDateFrom(f.date_from)
    if (f?.date_to) setDateTo(f.date_to)
    fetchReport()
  }

  const handleSaveComparison = async (type: 'month' | 'year') => {
    const now = new Date()
    const labels = { month: 'Dieser Monat', year: 'Dieses Jahr' }
    const compLabels = { month: 'Letzter Monat', year: 'Letztes Jahr' }

    let curFrom = monthStart
    let curTo = today
    let compFrom = ''
    let compTo = ''

    if (type === 'month') {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0)
      compFrom = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}-01`
      compTo = `${prevMonthEnd.getFullYear()}-${String(prevMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(prevMonthEnd.getDate()).padStart(2, '0')}`
    } else {
      const prevYear = now.getFullYear() - 1
      compFrom = `${prevYear}-01-01`
      compTo = `${prevYear}-12-31`
    }

    try {
      const payload: HRComparisonPeriodSave = {
        comparison_name: `${labels[type]} vs ${compLabels[type]}`,
        current_period_from: curFrom,
        current_period_to: curTo,
        current_period_label: labels[type],
        comparison_period_from: compFrom,
        comparison_period_to: compTo,
        comparison_period_label: compLabels[type],
        comparison_type: type === 'month' ? 'month_over_month' : 'year_over_year',
      }
      await apiClient.post('/api/v1/hr/comparison-periods', payload)
      fetchComparisonPeriods()
    } catch { setError('Vergleich konnte nicht gespeichert werden') }
  }

  const handleApplyComparison = async (cpId: string) => {
    const cp = comparisonPeriods.find(p => p.id === cpId)
    if (!cp) return
    try {
      const { data } = await apiClient.get<HRComparisonResponse>('/api/v1/hr/reports/compare-periods', {
        params: { period_type: 'month', compare_with_previous: true }
      })
      setComparisonData(data)
      setSelectedComparison(cpId)
    } catch { /* silently fail */ }
  }

  const handleExport = async (format: string) => {
    if (!reportData?.data?.length) return
    try {
      const params = new URLSearchParams({ format })
      if (selectedTemplateId) params.set('template_id', selectedTemplateId)
      const { data } = await apiClient.post(
        `/api/v1/hr/reports/export?${params.toString()}`,
        { data: reportData.data, format, report_type: activeReport },
        { responseType: 'blob' }
      )
      const url = URL.createObjectURL(new Blob([data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `bericht_${activeReport}.${format === 'xlsx' ? 'xlsx' : format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch { setError('Export fehlgeschlagen') }
  }

  const handleDownloadChart = async () => {
    if (!chartRef.current) return
    try {
      const svg = chartRef.current.querySelector('svg')
      if (!svg) return
      const svgData = new XMLSerializer().serializeToString(svg)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const img = new Image()
      img.onload = () => {
        canvas.width = img.width * 2
        canvas.height = img.height * 2
        ctx.scale(2, 2)
        ctx.drawImage(img, 0, 0)
        canvas.toBlob((blob) => {
          if (!blob) return
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `chart_${activeReport}.png`
          a.click()
          URL.revokeObjectURL(url)
        })
      }
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
    } catch { /* silently fail */ }
  }

  const paginatedData = reportData?.data
    ? reportData.data.slice((page - 1) * pageSize, page * pageSize)
    : []
  const totalPages = reportData?.data ? Math.ceil(reportData.data.length / pageSize) : 0

  if (moduleInactive) {
    return <HrModuleInactiveNotice title="HR Berichte & Statistiken" />
  }

  return (
    <div className="flex flex-col min-h-full">
      <HrPageHeader
        title="HR Berichte & Statistiken"
        description="Auswertungen zu Vertretungsvolumen, Auszahlungen und IPB-Salden — mit Diagrammen, Exporten und geplanten Reports."
      />

      <div className="flex-1 min-h-0 overflow-y-auto p-6">
      {/* Report Tabs — hr2-v2 (E9): Standard-Stil (indigo aktiv) */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-4">
        {REPORTS.map(r => (
          <button key={r.key} onClick={() => setActiveReport(r.key)}
            className={`px-3 py-1.5 text-sm font-medium transition border-b-2 -mb-px ${
              activeReport === r.key
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}>{r.label}</button>
        ))}
      </div>

      {/* Filter Bar — R9 (hr-v3): Datumsfilter nur bei datumsabhängigen Reports */}
      <form onSubmit={e => { e.preventDefault(); fetchReport() }} className="flex flex-wrap gap-3 items-end mb-4 p-3 bg-gray-50 rounded-lg">
        {activeReport !== 'ipb_saldo_distribution' ? (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Von</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="rounded border-gray-300 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Bis</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="rounded border-gray-300 text-sm" />
            </div>
          </>
        ) : (
          <div className="text-xs text-gray-500 pb-2">
            Die IPB-Salden-Verteilung ist datumsunabhängig (aktueller Saldo je Lehrer).
          </div>
        )}
        <button type="submit" disabled={loading}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Laden...' : 'Generieren'}
        </button>
        <button type="button" onClick={() => setShowFilters(!showFilters)}
          className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300">
          {showFilters ? 'Filter ausblenden' : 'Filter & Presets'}
        </button>
        <button type="button" onClick={() => setShowAdjustments(!showAdjustments)}
          className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-md text-sm hover:bg-amber-200">
          {showAdjustments ? 'Änderungen ausblenden' : 'Änderungen'}
        </button>
        {reportData && (
          <div className="flex gap-1 ml-auto items-center">
            <select value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)}
              className="text-xs border rounded px-1 py-0.5">
              <option value="">Kein Template</option>
              {templates.filter(t => t.is_default).map(t => <option key={t.id} value={t.id}>{t.template_name} ({t.export_format})</option>)}
            </select>
            <button type="button" onClick={() => handleOpenTemplateManager('csv')}
              className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs hover:bg-indigo-200">Vorlagen</button>
            <button type="button" onClick={() => handleExport('csv')}
              className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700">CSV</button>
            <button type="button" onClick={() => handleExport('xlsx')}
              className="px-2 py-1 bg-green-700 text-white rounded text-xs hover:bg-green-800">Excel</button>
            <button type="button" onClick={() => handleExport('pdf')}
              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700">PDF</button>
          </div>
        )}
      </form>

      {/* Export History */}
      {showExports && (
        <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
          <h3 className="text-sm font-semibold mb-3">Exportverlauf</h3>
          {exportHistory.length === 0 && <p className="text-xs text-gray-400">Keine Exporte</p>}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {exportHistory.map(e => (
              <div key={e.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                <div>
                  <span className="font-medium">{e.report_name}</span>
                  <span className="text-gray-500 mx-1 uppercase">{e.export_format}</span>
                  <span className="text-gray-400">{e.record_count ?? 0} DS · {(e.file_size_bytes / 1024).toFixed(0)} KB</span>
                  {e.is_scheduled && <span className="ml-1 px-1 bg-purple-100 text-purple-600 rounded text-xs">Auto</span>}
                </div>
                <div className="text-right text-gray-400">
                  <div>{new Date(e.exported_at).toLocaleDateString('de-CH')}</div>
                  <div>{e.download_count}x DL</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scheduled Reports */}
      {showScheduled && (
        <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Geplante Reports</h3>
            <button onClick={() => setShowCreateSchedule(!showCreateSchedule)}
              className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">+ Neu</button>
          </div>

          {showCreateSchedule && (
            <div className="mb-3 p-3 bg-gray-50 rounded space-y-2">
              <input type="text" value={newSchedule.schedule_name || ''}
                onChange={e => setNewSchedule(p => ({ ...p, schedule_name: e.target.value }))}
                className="w-full text-xs border rounded px-2 py-1" placeholder="Name..." />
              <div className="flex gap-2">
                <select value={newSchedule.report_type} onChange={e => setNewSchedule(p => ({ ...p, report_type: e.target.value }))}
                  className="text-xs border rounded px-2 py-1">
                  {REPORTS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
                <select value={newSchedule.export_format} onChange={e => setNewSchedule(p => ({ ...p, export_format: e.target.value }))}
                  className="text-xs border rounded px-2 py-1">
                  <option value="csv">CSV</option><option value="xlsx">Excel</option>
                </select>
                <select value={newSchedule.frequency} onChange={e => setNewSchedule(p => ({ ...p, frequency: e.target.value }))}
                  className="text-xs border rounded px-2 py-1">
                  <option value="daily">Täglich</option><option value="weekly">Wöchentlich</option><option value="monthly">Monatlich</option>
                </select>
                <input type="time" value={newSchedule.time_of_day || '08:00'}
                  onChange={e => setNewSchedule(p => ({ ...p, time_of_day: e.target.value }))}
                  className="text-xs border rounded px-2 py-1" />
              </div>
              <div className="flex gap-1">
                <input type="email" value={newScheduleEmail} onChange={e => setNewScheduleEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddScheduleEmail()}
                  className="flex-1 text-xs border rounded px-2 py-1" placeholder="E-Mail..." />
                <button onClick={handleAddScheduleEmail} className="px-2 py-1 bg-gray-200 text-xs rounded">+</button>
              </div>
              {(newSchedule.recipient_emails || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {newSchedule.recipient_emails?.map((em, i) => (
                    <span key={i} className="text-xs bg-blue-100 text-blue-700 px-1 py-0.5 rounded">{em}
                      <button onClick={() => setNewSchedule(p => ({ ...p, recipient_emails: p.recipient_emails?.filter((_, j) => j !== i) }))}
                        className="ml-1 text-blue-400 hover:text-blue-600">×</button>
                    </span>
                  ))}
                </div>
              )}
              <button onClick={handleCreateSchedule}
                className="w-full px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">Erstellen</button>
            </div>
          )}

          {schedules.length === 0 && !showCreateSchedule && <p className="text-xs text-gray-400">Keine geplanten Reports</p>}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {schedules.map(s => (
              <div key={s.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                <div>
                  <span className="font-medium">{s.schedule_name}</span>
                  <span className={`ml-1 px-1 rounded text-xs ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                    {s.is_active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                  <span className="text-gray-400 ml-1">{s.report_type} · {s.export_format.toUpperCase()} · {s.frequency}</span>
                  {s.failure_count > 0 && <span className="ml-1 text-red-500">⚠ {s.failure_count}</span>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleRunScheduleNow(s.id)} className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">▶</button>
                  <button onClick={() => handleToggleSchedule(s.id, !s.is_active)}
                    className={`px-1.5 py-0.5 rounded ${s.is_active ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'} hover:opacity-80`}>
                    {s.is_active ? '⏸' : '▶'}
                  </button>
                  <button onClick={() => handleDeleteSchedule(s.id)} className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded hover:bg-red-200">✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Adjustment History */}
      {showAdjustments && (
        <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
          <h3 className="text-sm font-semibold mb-3">Letzte manuelle Änderungen</h3>
          {adjustments.length === 0 && <p className="text-xs text-gray-400">Keine Änderungen</p>}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {adjustments.map(a => (
              <div key={a.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                <div>
                  <span className="font-medium">{a.field_name}</span>
                  <span className="text-gray-500 mx-1">{a.old_value ?? '-'} → {a.new_value}</span>
                  <span className="text-amber-600 ml-2 italic">"{a.adjustment_reason}"</span>
                </div>
                <div className="text-right text-gray-400">
                  <div>{a.performed_by_name}</div>
                  <div>{new Date(a.created_at).toLocaleDateString('de-CH')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter Sidebar */}
      {showFilters && (
        <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Presets */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Gespeicherte Presets</h3>
            {presets.length === 0 && <p className="text-xs text-gray-400">Keine Presets</p>}
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {presets.map(p => (
                <button key={p.id} onClick={() => handleApplyPreset(p)}
                  className="block w-full text-left px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200">
                  {p.name} <span className="text-gray-400">({p.category})</span>
                </button>
              ))}
            </div>
            <div className="flex gap-1 mt-2">
              <input type="text" value={presetName} onChange={e => setPresetName(e.target.value)}
                className="flex-1 text-xs border rounded px-2 py-1" placeholder="Preset Name..." />
              <button type="button" onClick={handleSavePreset}
                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">Speichern</button>
            </div>
          </div>

          {/* Comparison */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Periodenvergleich</h3>
            {/* hr2-v2 (E9): App-Toggle-Switch statt native Checkbox */}
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                id="comp-toggle"
                onClick={() => setComparisonEnabled(v => !v)}
                role="switch" aria-checked={comparisonEnabled} aria-label="Vergleich aktivieren"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${comparisonEnabled ? 'bg-indigo-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${comparisonEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <label htmlFor="comp-toggle" className="text-xs cursor-pointer" onClick={() => setComparisonEnabled(v => !v)}>Vergleich aktivieren</label>
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={() => handleSaveComparison('month')}
                className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded hover:bg-purple-200">Monat</button>
              <button type="button" onClick={() => handleSaveComparison('year')}
                className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded hover:bg-purple-200">Jahr</button>
            </div>
            {comparisonPeriods.length > 0 && (
              <select value={selectedComparison} onChange={e => handleApplyComparison(e.target.value)}
                className="mt-2 w-full text-xs border rounded px-2 py-1">
                <option value="">Gespeicherte Vergleiche...</option>
                {comparisonPeriods.map(cp => (
                  <option key={cp.id} value={cp.id}>{cp.comparison_name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Filter Category */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Filter Kategorie</h3>
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
              className="w-full text-sm border rounded px-2 py-1">
              <option value="all">Alle</option>
              {REPORTS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
        </div>
      )}

      {loading && <div className="text-center py-8">Laden...</div>}
      {error && <div className="p-3 bg-red-100 text-red-700 rounded mb-4">{error}</div>}

      {reportData && (
        <>
          {/* Summary Stats — hr2-v2 (E9): Kartendesign mit Ton-Farbe */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            {Object.entries(reportData.summary).map(([k, v]) => (
              <div key={k} className="p-3 bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-indigo-400 text-center">
                <div className="text-xs text-gray-500 capitalize">{k.replace(/_/g, ' ')}</div>
                <div className="text-lg font-bold">{typeof v === 'number' ? (v > 100 ? `${fmtNum(v)}%` : fmtNum(v, 2)) : String(v)}</div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {reportData.data.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-4xl mb-3">📭</div>
              <p className="text-gray-500 font-medium">Keine Daten für den gewählten Zeitraum</p>
              <p className="text-xs text-gray-400 mt-1">Versuchen Sie einen anderen Zeitraum oder Berichtstyp.</p>
            </div>
          )}

          {/* Comparison Delta */}
          {comparisonEnabled && comparisonData?.changes && (
            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-purple-700">Vergleich: {comparisonData?.current_period.total} vs {comparisonData?.previous_period?.total ?? '-'}</span>
              <span className={`text-lg font-bold ${
                comparisonData.changes.total >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {comparisonData.changes.total >= 0 ? '↑' : '↓'} {fmtNum(Math.abs(comparisonData.changes.total))}%
              </span>
            </div>
          )}

          {/* View Mode + Chart Controls — hr2-v2 (E9): Standard-Stil */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1 border-b border-gray-200">
              {(['chart', 'table', 'both'] as ViewMode[]).map(mode => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className={`px-3 py-1.5 text-sm font-medium transition border-b-2 -mb-px ${
                    viewMode === mode
                      ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}>{mode === 'chart' ? 'Diagramm' : mode === 'table' ? 'Tabelle' : 'Beides'}</button>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              {(viewMode === 'chart' || viewMode === 'both') && (
                <select value={chartType} onChange={e => setChartType(e.target.value as ChartType)}
                  className="text-sm border rounded px-2 py-1">
                  <option value="bar">Balken</option>
                  <option value="line">Linien</option>
                  <option value="area">Fläche</option>
                  <option value="pie">Kreis</option>
                </select>
              )}
              {(viewMode === 'chart' || viewMode === 'both') && (
                <button onClick={handleDownloadChart}
                  className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded hover:bg-indigo-200">
                  Chart PNG
                </button>
              )}
            </div>
          </div>

          {/* Chart + Table */}
          <div className={`grid gap-4 ${viewMode === 'both' ? 'md:grid-cols-2' : ''}`}>
            {/* Chart */}
            {(viewMode === 'chart' || viewMode === 'both') && chartData.length > 0 && (
              <div ref={chartRef} className="bg-white p-4 rounded-lg border border-gray-200">
                <ResponsiveContainer width="100%" height={350}>
                  {renderChart(chartType, chartData, comparisonEnabled)}
                </ResponsiveContainer>
              </div>
            )}

            {/* Table */}
            {(viewMode === 'table' || viewMode === 'both') && reportData.data.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {reportData.data[0] && Object.keys(reportData.data[0]).map(k => (
                          <th key={k} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{k.replace(/_/g, ' ')}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedData.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          {Object.values(row).map((v, j) => (
                            <td key={j} className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                              {v !== null && v !== undefined ? String(v) : '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200">
                    <span className="text-xs text-gray-500">Seite {page} von {totalPages}</span>
                    <div className="flex gap-1">
                      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                        className="px-2 py-1 text-xs border rounded disabled:opacity-50">Zurück</button>
                      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                        className="px-2 py-1 text-xs border rounded disabled:opacity-50">Weiter</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="text-xs text-gray-400 mt-3">Generiert: {new Date(reportData.generated_at).toLocaleString('de-CH')}</div>
        </>
      )}

      {/* Template Manager Modal */}
      {showTemplateManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" style={{ maxWidth: '900px' }}>
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold">Export-Vorlagen verwalten</h2>
              <button onClick={() => setShowTemplateManager(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              {/* Format tabs */}
              <div className="flex gap-2 mb-4">
                {(['csv', 'xlsx', 'pdf'] as const).map(f => (
                  <button key={f} onClick={() => { setTemplateTab(f); setActiveTemplate(null) }}
                    className={`px-3 py-1 text-sm rounded ${templateTab === f ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>{f.toUpperCase()}</button>
                ))}
              </div>
              {/* Template list */}
              <div className="mb-4">
                <div className="flex gap-2 items-center mb-2">
                  {templates.filter(t => t.export_format === templateTab).map(t => (
                    <button key={t.id} onClick={() => setActiveTemplate(t)}
                      className={`px-3 py-1 text-xs rounded ${activeTemplate?.id === t.id ? 'bg-blue-100 border border-blue-400' : 'bg-gray-100'}`}>
                      {t.template_name} {t.is_default && <span className="text-green-600">★</span>}
                    </button>
                  ))}
                  <button onClick={handleResetDefaults} className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200">Defaults wiederherstellen</button>
                </div>
              </div>
              {/* Create new */}
              <div className="flex gap-2 mb-4">
                <input type="text" value={templateName} onChange={e => setTemplateName(e.target.value)}
                  className="flex-1 text-sm border rounded px-2 py-1" placeholder="Neuer Template-Name..." />
                <button onClick={handleSaveTemplate} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Erstellen</button>
              </div>
              {/* Edit columns */}
              {activeTemplate && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold">Spaltenkonfiguration: {activeTemplate.template_name}</h3>
                  {/* Color scheme */}
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs font-medium mb-2">Farbschema</div>
                    <div className="flex gap-2 mb-2">
                      {COLOR_PRESETS.map(cp => (
                        <button key={cp.name} onClick={() => { setTemplateColors(cp); handleUpdateTemplate(activeTemplate.id, { template_config: { ...activeTemplate.template_config, color_scheme: cp } }) }}
                          className={`px-2 py-1 text-xs rounded border ${(templateColors || COLOR_PRESETS[0]!).header_bg === cp.header_bg! ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}
                          style={{ backgroundColor: cp.header_bg, color: cp.header_fg }}>{cp.name}</button>
                      ))}
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      {(['header_bg', 'header_fg', 'row_bg', 'alt_row_bg'] as const).map(k => (
                        <div key={k}>
                          <label className="text-gray-500">{k.replace('_', ' ')}</label>
                          <input type="color" value={(templateColors as Record<string, string>)[k] || '#ffffff'}
                            onChange={e => { const nc = { ...(templateColors || COLOR_PRESETS[0]!), [k]: e.target.value }; setTemplateColors(nc); handleUpdateTemplate(activeTemplate.id, { template_config: { ...activeTemplate.template_config, color_scheme: nc } }) }}
                            className="w-full h-6 cursor-pointer" />
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Logo upload */}
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs font-medium mb-2">Logo</div>
                    <div className="flex gap-2 items-center">
                      <input type="file" accept="image/png,image/jpeg" onChange={e => e.target.files?.[0] && handleUploadLogo(e.target.files[0])}
                        className="text-xs" />
                      {logoPreview && <img src={logoPreview} alt="Logo" className="h-8 rounded" />}
                    </div>
                  </div>
                  {/* Columns */}
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {Object.entries(activeTemplate.column_mapping).map(([key, col]) => (
                      <div key={key} className="flex items-center gap-2 text-xs p-1 bg-gray-50 rounded">
                        <input type="checkbox" checked={col.visible}
                          onChange={() => { const nc = { ...activeTemplate.column_mapping, [key]: { ...col, visible: !col.visible } }; setActiveTemplate({ ...activeTemplate, column_mapping: nc }); }}
                          className="rounded" />
                        {editingCol === key ? (
                          <input type="text" value={col.label} autoFocus
                            onBlur={e => { handleUpdateTemplate(activeTemplate.id, { column_mapping: { ...activeTemplate.column_mapping, [key]: { ...col, label: e.target.value } } }); setEditingCol(null) }}
                            onKeyDown={e => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                            className="w-32 border rounded px-1 py-0.5" />
                        ) : (
                          <span onClick={() => setEditingCol(key)} className="cursor-pointer font-medium w-32 truncate hover:underline">{col.label}</span>
                        )}
                        <span className="text-gray-400 w-24">{key}</span>
                        <input type="number" value={col.width_px || 120}
                          onChange={e => { const nc = { ...activeTemplate.column_mapping, [key]: { ...col, width_px: parseInt(e.target.value) || 120 } }; setActiveTemplate({ ...activeTemplate, column_mapping: nc }) }}
                          onBlur={e => handleUpdateTemplate(activeTemplate.id, { column_mapping: { ...activeTemplate.column_mapping, [key]: { ...col, width_px: parseInt(e.target.value) || 120 } } })}
                          className="w-16 border rounded px-1 py-0.5 text-right" />
                        <span className="text-gray-400">px</span>
                        {activeTemplate.is_default || (
                          <button onClick={() => handleDeleteTemplate(activeTemplate.id)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
                        )}
                      </div>
                    ))}
                  </div>
                 </div>
               )}
             </div>
           </div>
         </div>
       )}
      </div>
    </div>
  )
}

function transformToChartData(data: HRReportResponse | null): Array<Record<string, unknown>> {
  if (!data?.data?.length) return []
  const keys = Object.keys(data.data[0]!)
  const nameKey = keys.find(k => k.includes('name') || k.includes('teacher') || k.includes('date')) || keys[0]!
  return data.data.map(row => {
    const entry: Record<string, unknown> = { name: String(row[nameKey!] ?? '-') }
    for (const [k, v] of Object.entries(row)) {
      if (typeof v === 'number') entry[k] = v
    }
    return entry
  })
}

function renderChart(type: ChartType, data: Array<Record<string, unknown>>, showComparison: boolean) {
  if (!data.length) {
    return <div className="flex items-center justify-center h-full text-gray-400 text-sm">Keine Daten für Diagramm verfügbar</div>
  }
  const numericKeys = Object.keys(data[0]!).filter(k => k !== 'name' && typeof data[0]![k] === 'number')
  const primaryKey = numericKeys[0] ?? 'total_requested'
  const secondaryKey = numericKeys[1] ?? 'total_assigned'

  const grid = <CartesianGrid strokeDasharray="3 3" />
  const xAxis = <XAxis dataKey="name" tick={{ fontSize: 10 }} />
  const yAxis = <YAxis />
  const tooltip = <Tooltip />
  const legend = <Legend />

  switch (type) {
    case 'bar':
      return <BarChart data={data}>
        {grid}{xAxis}{yAxis}{tooltip}{legend}
        <Bar dataKey={primaryKey} fill={COLORS[0]} name={primaryKey} />
        {numericKeys.length > 1 && <Bar dataKey={secondaryKey} fill={COLORS[1]} name={secondaryKey} />}
        {showComparison && <Bar dataKey="comparison_value" fill={COLORS[0]} fillOpacity={0.3} name="Vergleich" />}
      </BarChart>
    case 'line':
      return <LineChart data={data}>
        {grid}{xAxis}{yAxis}{tooltip}{legend}
        <Line type="monotone" dataKey={primaryKey} stroke={COLORS[0]} name={primaryKey} />
        {numericKeys.length > 1 && <Line type="monotone" dataKey={secondaryKey} stroke={COLORS[1]} name={secondaryKey} />}
        {showComparison && <Line type="monotone" dataKey="comparison_value" stroke={COLORS[0]} strokeDasharray="5 5" name="Vergleich" />}
      </LineChart>
    case 'area':
      return <AreaChart data={data}>
        {grid}{xAxis}{yAxis}{tooltip}{legend}
        <Area type="monotone" dataKey={primaryKey} stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.3} name={primaryKey} />
        {numericKeys.length > 1 && <Area type="monotone" dataKey={secondaryKey} stroke={COLORS[1]} fill={COLORS[1]} fillOpacity={0.3} name={secondaryKey} />}
      </AreaChart>
    case 'pie': {
      const total = data.reduce((sum, d) => sum + (Number(d[primaryKey]) || 0), 0)
      const pieData = [
        { name: 'Erster Wert', value: data[0]?.[primaryKey] as number ?? 0 },
        { name: 'Rest', value: total - (data[0]?.[primaryKey] as number ?? 0) }
      ]
      return <PieChart>
        <Pie data={pieData} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}`}
          outerRadius={120} fill="#8884d8" dataKey="value">
          {pieData.map((_, i) => <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        {tooltip}{legend}
      </PieChart>
    }
    default:
      return null
  }
}
